/**
 * Sistema Centralizado de Logging - Odin Sight
 *
 * Gerencia logs pertinentes para o usuário final nas seguintes áreas:
 * - Conexão de dados (SignalR)
 * - Leitura de dados de mercado
 * - Salvamento/carregamento de desktop
 * - Ordens de trading
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import logBatcher from "./logBatcher";

// Remote log shipping flag — flipped by marketdata.jsx after login based on the
// RemoteLogging field returned in the token response. When on, every addLog()
// forwards to logBatcher for remote persistence in MongoDB. Default off.
let remoteShippingEnabled = false;
const remoteShippingListeners = new Set();

export function setRemoteShipping(enabled) {
  const next = !!enabled;
  if (next === remoteShippingEnabled) return;
  remoteShippingEnabled = next;
  logBatcher.setEnabled(next);
  remoteShippingListeners.forEach((fn) => {
    try {
      fn(next);
    } catch (err) {
      console.warn("[logger] listener threw", err);
    }
  });
}

export function isRemoteShippingEnabled() {
  return remoteShippingEnabled;
}

export function onRemoteShippingChange(fn) {
  remoteShippingListeners.add(fn);
  return () => remoteShippingListeners.delete(fn);
}

// Server-side revocation: logBatcher emits this when the batch endpoint returns
// 403. Mirror it back into the logger state so UI listeners (footer indicator)
// update immediately.
if (typeof window !== "undefined") {
  window.addEventListener("remote-log-disabled", () => {
    if (remoteShippingEnabled) setRemoteShipping(false);
  });
}

// Tipos de severidade para logs
export const LOG_LEVELS = {
  INFO: "info",
  WARNING: "warning",
  ERROR: "error",
  SUCCESS: "success",
};

// Categorias de logs
export const LOG_CATEGORIES = {
  CONNECTION: "connection",
  DATA_LOADING: "data_loading",
  WORKSPACE: "workspace",
  SYSTEM: "system",
  ORDERS: "orders",
  ALERT: "alert",
  AUTH: "auth",
  DIAGNOSTIC: "diagnostic",
};

// Store para gerenciamento de logs
export const useLogStore = create(
  persist(
    (set, get) => ({
      logs: [],
      maxLogs: 100, // Máximo de logs mantidos em memória

      // Adicionar novo log.
      // `silent` logs skip the in-memory `logs` array — they never appear in the
      // Log panel — but still go to the console, error reporting and remote
      // shipping. Used for diagnostic/instrumentation logs (see logger.diagnostic).
      addLog: (level, category, message, details = null, silent = false) => {
        const timestamp = new Date().toISOString();
        const time = new Date().toLocaleTimeString("pt-BR", {
          hour12: false,
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        });

        const logEntry = {
          id: Date.now() + Math.random(),
          timestamp,
          time,
          level,
          category,
          message,
          details,
        };

        // Diagnostic (silent) logs are not surfaced in the operational Log panel.
        if (!silent) {
          set((state) => {
            const newLogs = [logEntry, ...state.logs];

            // Manter apenas os logs mais recentes
            if (newLogs.length > state.maxLogs) {
              newLogs.splice(state.maxLogs);
            }

            return { logs: newLogs };
          });
        }

        // Log no console para desenvolvimento
        const emoji = getLogEmoji(level);
        console.log(`${emoji} [${time}] ${message}`, details || "");

        // Report errors to server
        if (level === LOG_LEVELS.ERROR) {
          import("./errorReportService")
            .then(({ default: errorReporter }) => {
              errorReporter.report(
                new Error(message),
                { details },
                {
                  errorType: "LoggedError",
                  component: category,
                  additionalContext: { category, details },
                },
              );
            })
            .catch((err) => {
              console.warn("Failed to report error to server:", err);
            });
        }

        // Remote log shipping — forward every entry (all levels) when the user
        // is whitelisted. The batcher gates internally, so this is safe to call
        // unconditionally, but the flag check avoids the function-call cost on
        // the hot path for non-whitelisted users (the default).
        if (remoteShippingEnabled) {
          try {
            logBatcher.push({ timestamp, level, category, message, details });
          } catch (err) {
            console.warn("[logger] logBatcher.push failed", err);
          }
        }
      },

      // Limpar todos os logs
      clearLogs: () => set({ logs: [] }),

      // Obter logs por categoria
      getLogsByCategory: (category) => {
        return get().logs.filter((log) => log.category === category);
      },

      // Obter logs por nível
      getLogsByLevel: (level) => {
        return get().logs.filter((log) => log.level === level);
      },

      // Obter estatísticas de logs
      getLogStats: () => {
        const logs = get().logs;
        return {
          total: logs.length,
          byLevel: {
            info: logs.filter((l) => l.level === LOG_LEVELS.INFO).length,
            warning: logs.filter((l) => l.level === LOG_LEVELS.WARNING).length,
            error: logs.filter((l) => l.level === LOG_LEVELS.ERROR).length,
            success: logs.filter((l) => l.level === LOG_LEVELS.SUCCESS).length,
          },
          byCategory: {
            connection: logs.filter(
              (l) => l.category === LOG_CATEGORIES.CONNECTION,
            ).length,
            data_loading: logs.filter(
              (l) => l.category === LOG_CATEGORIES.DATA_LOADING,
            ).length,
            workspace: logs.filter(
              (l) => l.category === LOG_CATEGORIES.WORKSPACE,
            ).length,
            system: logs.filter((l) => l.category === LOG_CATEGORIES.SYSTEM)
              .length,
            orders: logs.filter((l) => l.category === LOG_CATEGORIES.ORDERS)
              .length,
            alert: logs.filter((l) => l.category === LOG_CATEGORIES.ALERT)
              .length,
            auth: logs.filter((l) => l.category === LOG_CATEGORIES.AUTH).length,
          },
        };
      },
    }),
    {
      name: "odin-sight-logs", // localStorage key
      // Limitar logs persistidos para não sobrecarregar o localStorage
      partialize: (state) => ({
        logs: state.logs.slice(0, 50), // Persistir apenas os 50 logs mais recentes
      }),
    },
  ),
);

// Função helper para obter emoji baseado no nível
const getLogEmoji = (level) => {
  switch (level) {
    case LOG_LEVELS.INFO:
      return "ℹ️";
    case LOG_LEVELS.WARNING:
      return "⚠️";
    case LOG_LEVELS.ERROR:
      return "❌";
    case LOG_LEVELS.SUCCESS:
      return "✅";
    default:
      return "📝";
  }
};

// Logger principal - interface simplificada para uso
export const logger = {
  // Conexão
  connection: {
    info: (message, details) =>
      useLogStore
        .getState()
        .addLog(LOG_LEVELS.INFO, LOG_CATEGORIES.CONNECTION, message, details),
    warning: (message, details) =>
      useLogStore
        .getState()
        .addLog(
          LOG_LEVELS.WARNING,
          LOG_CATEGORIES.CONNECTION,
          message,
          details,
        ),
    error: (message, details) =>
      useLogStore
        .getState()
        .addLog(LOG_LEVELS.ERROR, LOG_CATEGORIES.CONNECTION, message, details),
    success: (message, details) =>
      useLogStore
        .getState()
        .addLog(
          LOG_LEVELS.SUCCESS,
          LOG_CATEGORIES.CONNECTION,
          message,
          details,
        ),
  },

  // Carregamento de dados
  data: {
    info: (message, details) =>
      useLogStore
        .getState()
        .addLog(LOG_LEVELS.INFO, LOG_CATEGORIES.DATA_LOADING, message, details),
    warning: (message, details) =>
      useLogStore
        .getState()
        .addLog(
          LOG_LEVELS.WARNING,
          LOG_CATEGORIES.DATA_LOADING,
          message,
          details,
        ),
    error: (message, details) =>
      useLogStore
        .getState()
        .addLog(
          LOG_LEVELS.ERROR,
          LOG_CATEGORIES.DATA_LOADING,
          message,
          details,
        ),
    success: (message, details) =>
      useLogStore
        .getState()
        .addLog(
          LOG_LEVELS.SUCCESS,
          LOG_CATEGORIES.DATA_LOADING,
          message,
          details,
        ),
  },

  // Workspace
  workspace: {
    info: (message, details) =>
      useLogStore
        .getState()
        .addLog(LOG_LEVELS.INFO, LOG_CATEGORIES.WORKSPACE, message, details),
    warning: (message, details) =>
      useLogStore
        .getState()
        .addLog(LOG_LEVELS.WARNING, LOG_CATEGORIES.WORKSPACE, message, details),
    error: (message, details) =>
      useLogStore
        .getState()
        .addLog(LOG_LEVELS.ERROR, LOG_CATEGORIES.WORKSPACE, message, details),
    success: (message, details) =>
      useLogStore
        .getState()
        .addLog(LOG_LEVELS.SUCCESS, LOG_CATEGORIES.WORKSPACE, message, details),
  },

  // Sistema geral
  system: {
    info: (message, details) =>
      useLogStore
        .getState()
        .addLog(LOG_LEVELS.INFO, LOG_CATEGORIES.SYSTEM, message, details),
    warning: (message, details) =>
      useLogStore
        .getState()
        .addLog(LOG_LEVELS.WARNING, LOG_CATEGORIES.SYSTEM, message, details),
    error: (message, details) =>
      useLogStore
        .getState()
        .addLog(LOG_LEVELS.ERROR, LOG_CATEGORIES.SYSTEM, message, details),
    success: (message, details) =>
      useLogStore
        .getState()
        .addLog(LOG_LEVELS.SUCCESS, LOG_CATEGORIES.SYSTEM, message, details),
  },

  // Ordens de trading
  orders: {
    info: (message, details) =>
      useLogStore
        .getState()
        .addLog(LOG_LEVELS.INFO, LOG_CATEGORIES.ORDERS, message, details),
    warning: (message, details) =>
      useLogStore
        .getState()
        .addLog(LOG_LEVELS.WARNING, LOG_CATEGORIES.ORDERS, message, details),
    error: (message, details) =>
      useLogStore
        .getState()
        .addLog(LOG_LEVELS.ERROR, LOG_CATEGORIES.ORDERS, message, details),
    success: (message, details) =>
      useLogStore
        .getState()
        .addLog(LOG_LEVELS.SUCCESS, LOG_CATEGORIES.ORDERS, message, details),
  },

  // Alertas de preço
  alert: {
    info: (message, details) =>
      useLogStore
        .getState()
        .addLog(LOG_LEVELS.INFO, LOG_CATEGORIES.ALERT, message, details),
    warning: (message, details) =>
      useLogStore
        .getState()
        .addLog(LOG_LEVELS.WARNING, LOG_CATEGORIES.ALERT, message, details),
    error: (message, details) =>
      useLogStore
        .getState()
        .addLog(LOG_LEVELS.ERROR, LOG_CATEGORIES.ALERT, message, details),
    success: (message, details) =>
      useLogStore
        .getState()
        .addLog(LOG_LEVELS.SUCCESS, LOG_CATEGORIES.ALERT, message, details),
  },

  // Autenticação
  auth: {
    info: (message, details) =>
      useLogStore
        .getState()
        .addLog(LOG_LEVELS.INFO, LOG_CATEGORIES.AUTH, message, details),
    warning: (message, details) =>
      useLogStore
        .getState()
        .addLog(LOG_LEVELS.WARNING, LOG_CATEGORIES.AUTH, message, details),
    error: (message, details) =>
      useLogStore
        .getState()
        .addLog(LOG_LEVELS.ERROR, LOG_CATEGORIES.AUTH, message, details),
    success: (message, details) =>
      useLogStore
        .getState()
        .addLog(LOG_LEVELS.SUCCESS, LOG_CATEGORIES.AUTH, message, details),
  },

  // Diagnóstico / instrumentação — NÃO aparece no painel de log operacional.
  // Vai apenas para o console e, quando o REC (remote log shipping) está ativo
  // para o cliente, para o MongoDB. Use para snapshots, visibilidade, churn de
  // subscription e demais sinais de debug.
  diagnostic: {
    info: (message, details) =>
      useLogStore
        .getState()
        .addLog(
          LOG_LEVELS.INFO,
          LOG_CATEGORIES.DIAGNOSTIC,
          message,
          details,
          true,
        ),
    warning: (message, details) =>
      useLogStore
        .getState()
        .addLog(
          LOG_LEVELS.WARNING,
          LOG_CATEGORIES.DIAGNOSTIC,
          message,
          details,
          true,
        ),
    error: (message, details) =>
      useLogStore
        .getState()
        .addLog(
          LOG_LEVELS.ERROR,
          LOG_CATEGORIES.DIAGNOSTIC,
          message,
          details,
          true,
        ),
    success: (message, details) =>
      useLogStore
        .getState()
        .addLog(
          LOG_LEVELS.SUCCESS,
          LOG_CATEGORIES.DIAGNOSTIC,
          message,
          details,
          true,
        ),
  },
};

export default logger;
